// Renders a `run-completed` `finalAnswer` (or a persisted assistant message's `content`, the same
// raw string shape — see `../useAgenticRunController.js`) as full GitHub-flavored Markdown, with
// this pipeline's own `[[type:len:payload]]` (document/entity/citation chips) and `[[cite:id,...]]`
// (inline citation pills) markers spliced into the rendered tree — see
// `./markdown/AgenticMarkdownComponents.jsx`/`./markdown/agenticMarkdownMarkers.js`.
//
// This used to render pre-decoded `segments` as plain, whitespace-preserving prose specifically to
// avoid Markdown, because splitting the answer into independently-parsed chunks around each chip
// risked breaking block-level constructs (a list or table straddling a chip). That risk is now
// avoided a different way: the WHOLE raw `text` is parsed as ONE markdown document (never split),
// and marker substitution happens AFTER parsing, inside each already-rendered element's children —
// so a `- [[document:len:{...}]]` list item (real backend output, see
// `dash-api/services/ai-assistant/src/loop/finalize-answer.ts`) parses as one normal list item and
// only its rendered text content gets the chip substitution.
import React, {useMemo, useState, useCallback, useEffect} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {Box, IconButton, Stack, Tooltip} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import {buildAgenticMarkdownComponents} from './markdown/AgenticMarkdownComponents';
import {protectInlineComponentMarkers} from './markdown/agenticMarkdownMarkers';
import {normalizeMarkdownLists} from './markdown/normalizeMarkdownLists';
import {stripChartOpsFromStreamingText} from '../stripChartOpsFromStreamingText';
import {CitationsPayloadDialog} from '../../components/messageRows/markdown/CitationsMarkdownComponents';
import {useCitationsDialog} from '../../components/messageRows/markdown/useCitationsDialog';
import {cleanAiAssistantCopyText} from '../../utils/wysiwygCopy';
import {submitRunFeedback} from '../../services/agenticRun/agenticRunApi';

/** @param {{
 *   text: string,
 *   citations?: Array<{citationId: string, reportId: string, segmentId: number}>,
 *   onSelectComponent?: Function,
 *   runId?: string|null,
 *   initialRating?: 'thumbs_up'|'thumbs_down'|null,
 *   onRatingChange?: (rating: 'thumbs_up'|'thumbs_down'|null) => void,
 *   apiBaseUrl?: string,
 * }} props */
const AgenticFinalAnswer = ({
    text = '',
    citations = [],
    onSelectComponent,
    runId = null,
    initialRating = null,
    onRatingChange,
    apiBaseUrl,
}) => {
    const [copied, setCopied] = useState(false);
    const [rating, setRating] = useState(initialRating ?? null);

    useEffect(() => {
        setRating(initialRating ?? null);
    }, [runId, initialRating]);

    const {
        citationsDialogState,
        handleOpenCitations,
        handleCloseCitations,
        handleRetryCitations,
    } = useCitationsDialog();

    const {protectedText, chips} = useMemo(
        () => protectInlineComponentMarkers(stripChartOpsFromStreamingText(text)),
        [text]
    );

    const markdownComponents = useMemo(
        () => buildAgenticMarkdownComponents({
            citations,
            onSelectComponent,
            onOpenCitations: handleOpenCitations,
            sourceText: text,
            protectedChips: chips,
        }),
        [citations, onSelectComponent, handleOpenCitations, text, chips]
    );

    const normalizedText = useMemo(() => normalizeMarkdownLists(protectedText), [protectedText]);

    const handleCopy = useCallback(() => {
        const clean = cleanAiAssistantCopyText(text, {citations});
        if (!clean) {
            return;
        }
        navigator.clipboard.writeText(clean).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
    }, [text, citations]);

    const handleRating = useCallback((value) => {
        if (!runId) {
            return;
        }
        const next = rating === value ? null : value;
        setRating(next);
        onRatingChange?.(next);
        submitRunFeedback(runId, next, apiBaseUrl).catch(() => {
            setRating(rating);
            onRatingChange?.(rating);
        });
    }, [runId, rating, apiBaseUrl, onRatingChange]);

    if (!text) {
        return null;
    }

    return (
        <Box
            data-testid="agentic-final-answer"
            sx={{
                fontSize: 13 * 1.25,
                lineHeight: 1.6,
                '& p': {mt: 0, mb: 0.75},
                '& h1': {fontSize: 16 * 1.25, fontWeight: 600, mt: 1.5, mb: 0.5},
                '& h2': {fontSize: 15 * 1.25, fontWeight: 600, mt: 1.5, mb: 0.5},
                '& h3': {fontSize: 14 * 1.25, fontWeight: 600, mt: 1.25, mb: 0.5},
                '& h4, & h5, & h6': {fontSize: 13 * 1.25, fontWeight: 600, mt: 1, mb: 0.25},
                '& ul, & ol': {my: 0.5},
                '& li': {mb: 0.25},
                '& code': {fontSize: 12 * 1.25},
                '& table': {fontSize: 12 * 1.25},
            }}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{normalizedText}</ReactMarkdown>
            <Box
                data-testid="agentic-final-answer-actions"
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 0.5,
                    mr: -0.5,
                }}
            >
                <Stack direction="row" spacing={0} alignItems="center">
                    <Tooltip title={rating === 'thumbs_up' ? 'Remove rating' : 'Good response'} placement="top">
                        <IconButton
                            size="small"
                            onClick={() => handleRating('thumbs_up')}
                            aria-label="Good response"
                            aria-pressed={rating === 'thumbs_up'}
                            sx={{color: rating === 'thumbs_up' ? 'success.main' : 'text.secondary'}}
                        >
                            {rating === 'thumbs_up'
                                ? <ThumbUpRoundedIcon sx={{fontSize: 15}}/>
                                : <ThumbUpOutlinedIcon sx={{fontSize: 15}}/>
                            }
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={rating === 'thumbs_down' ? 'Remove rating' : 'Poor response'} placement="top">
                        <IconButton
                            size="small"
                            onClick={() => handleRating('thumbs_down')}
                            aria-label="Poor response"
                            aria-pressed={rating === 'thumbs_down'}
                            sx={{color: rating === 'thumbs_down' ? 'error.main' : 'text.secondary'}}
                        >
                            {rating === 'thumbs_down'
                                ? <ThumbDownRoundedIcon sx={{fontSize: 15}}/>
                                : <ThumbDownOutlinedIcon sx={{fontSize: 15}}/>
                            }
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={copied ? 'Copied!' : 'Copy response'} placement="top">
                        <IconButton
                            size="small"
                            onClick={handleCopy}
                            aria-label="Copy response"
                            sx={{color: 'text.secondary'}}
                        >
                            {copied
                                ? <CheckRoundedIcon sx={{fontSize: 16, color: 'success.main'}}/>
                                : <ContentCopyRoundedIcon sx={{fontSize: 16}}/>
                            }
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <CitationsPayloadDialog
                open={citationsDialogState.open}
                payload={citationsDialogState.payload}
                requestStatus={citationsDialogState.requestStatus}
                responsePayload={citationsDialogState.responsePayload}
                errorMessage={citationsDialogState.errorMessage}
                onClose={handleCloseCitations}
                onRetry={handleRetryCitations}
            />
        </Box>
    );
};

export default AgenticFinalAnswer;
